// Constants for reuse
const CORS_PROXIES = [
  "https://corsproxy.io/?",
  "https://api.allorigins.win/raw?url=",
  "https://cors-anywhere.herokuapp.com/",
];

const USER_AGENT =
  "Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/91.0.4472.124 Safari/537.36";

// Default Twitter profile image as fallback
const DEFAULT_TWITTER_PROFILE_IMAGE =
  "https://abs.twimg.com/sticky/default_profile_images/default_profile_400x400.png";

// URL utilities
export const URL_UTILS = {
  // Check if string is a complete URL
  isUrl: (str) => {
    if (!str || str.length < 3 || str.length > 2000) return false;

    const pattern = new RegExp(
      "^(https?:\\/\\/)?" + // protocol
        "(([a-z\\d]([a-z\\d-]*[a-z\\d])*\\.)+[a-z]{2,}|" + // domain name
        "(\\d{1,3}\\.){3}\\d{1,3})" + // OR ip (v4) address
        "(\\:\\d+)?(\\/[-a-z\\d%@_.~+&:]*)*" + // port and path
        "(\\?[;&a-z\\d%@_.,~+&:=-]*)?" + // query string
        "(\\#[-a-z\\d_]*)?$", // fragment locator
      "i"
    );

    return pattern.test(str);
  },

  // Normalize URL by removing trailing punctuation
  normalize: (url) => url?.replace(/[\s.,!?]$/, ""),

  // Add protocol if missing
  ensureProtocol: (url) => {
    if (!url) return null;
    return url.startsWith("http") ? url : `https://${url}`;
  },

  // Extract patterns from text
  patterns: {
    protocol: /(https?:\/\/[^\s]+)(?:\s|$|[.,!?])/g,
    domain:
      /\b((?:www\.)?[a-z0-9][-a-z0-9]*[a-z0-9]\.(?:com|org|net|edu|gov|io|co|ai|app|dev|me|info|biz|xyz|online)[^\s]*)(?:\s|$|[.,!?])/gi,
  },
};

// Image utility functions
const IMAGE_UTILS = {
  // Get file extension from content type
  getFileExtension: (contentType) => {
    if (!contentType) return "jpg";
    if (contentType.includes("png")) return "png";
    if (contentType.includes("gif")) return "gif";
    if (contentType.includes("webp")) return "webp";
    return "jpg";
  },

  // Get image dimensions
  getDimensions: (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () =>
          resolve({
            widthPx: img.width,
            heightPx: img.height,
          });
        img.onerror = reject;
        img.src = e.target.result;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  },
};

// Result handler utility
const createResult = (success, data = null, error = null) => {
  return {
    success,
    ...(data && { ...data }),
    ...(error && { error: typeof error === "string" ? error : error.message }),
  };
};

// Authentication helper
const getAuthToken = async () => {
  try {
    const { validateAccessToken, refreshAccessToken } = await import(
      "@/axiosAuth"
    );
    const { useAuthStore } = await import("@/stores/auth");
    const authStore = useAuthStore();

    if (!authStore.authenticated || !authStore.accessToken) {
      return { success: false, error: "Authentication required" };
    }

    const isValid = await validateAccessToken(authStore.accessToken);
    if (!isValid) {
      await refreshAccessToken();
    }

    return { success: true, token: authStore.accessToken };
  } catch (error) {
    return { success: false, error: "Token validation failed" };
  }
};

// Retry mechanism for critical operations
const retry = async (fn, options = { maxAttempts: 3, delay: 500 }) => {
  let lastError;

  for (let attempt = 1; attempt <= options.maxAttempts; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      if (attempt < options.maxAttempts) {
        await new Promise((resolve) => setTimeout(resolve, options.delay));
      }
    }
  }

  throw lastError;
};

// Helper function to truncate text to a maximum length
const truncateText = (text, maxLength = 1000) => {
  if (!text || text.length <= maxLength) return text;
  return text.substring(0, maxLength).trim();
};

/**
 * Extract metadata from HTML document
 * @param {Document} doc - Parsed HTML document
 * @param {string} url - Original URL for resolving relative paths
 * @returns {Object} - Extracted metadata
 */
const extractMetadata = (doc, url) => {
  // Get title
  const ogTitleMeta = doc.querySelector('meta[property="og:title"]');
  let title = ogTitleMeta ? ogTitleMeta.getAttribute("content") : "";
  if (!title) {
    const twitterTitleMeta = doc.querySelector('meta[name="twitter:title"]');
    title = twitterTitleMeta ? twitterTitleMeta.getAttribute("content") : "";
  }
  if (!title) {
    title = doc.querySelector("title")?.textContent || "";
  }

  // Truncate title to 255 characters
  title = truncateText(title, 255);

  // Get description
  const ogDescriptionMeta = doc.querySelector(
    'meta[property="og:description"]'
  );
  let description = ogDescriptionMeta
    ? ogDescriptionMeta.getAttribute("content")
    : "";
  if (!description) {
    const twitterDescMeta = doc.querySelector(
      'meta[name="twitter:description"]'
    );
    description = twitterDescMeta
      ? twitterDescMeta.getAttribute("content")
      : "";
  }
  if (!description) {
    const descriptionMeta = doc.querySelector('meta[name="description"]');
    description = descriptionMeta
      ? descriptionMeta.getAttribute("content")
      : "";
  }

  // Truncate description to 1000 characters
  description = truncateText(description, 1000);

  // Get image
  const ogImageMeta = doc.querySelector('meta[property="og:image"]');
  let image = ogImageMeta ? ogImageMeta.getAttribute("content") : "";
  if (!image) {
    const twitterImageMeta = doc.querySelector('meta[name="twitter:image"]');
    image = twitterImageMeta ? twitterImageMeta.getAttribute("content") : "";
  }

  // Make image URL absolute
  if (image && !image.startsWith("http")) {
    image = new URL(image, url).href;
  }

  return {
    title,
    description,
    image,
    url, // Include the original URL
  };
};

/**
 * Try fetching a URL with multiple CORS proxies
 * @param {string} url - Original URL to fetch
 * @param {Object} options - Fetch options
 * @returns {Promise<Object>} - { success, response, html }
 */
const fetchWithProxies = async (url, options = {}) => {
  const defaultOptions = {
    method: "GET",
    headers: {
      Accept: "text/html",
      "User-Agent": USER_AGENT,
    },
    timeout: 5000,
  };

  const fetchOptions = { ...defaultOptions, ...options };

  for (let i = 0; i < CORS_PROXIES.length; i++) {
    try {
      const proxyUrl = CORS_PROXIES[i] + encodeURIComponent(url);
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      try {
        const response = await fetch(proxyUrl, {
          ...fetchOptions,
          signal: controller.signal,
        });

        clearTimeout(timeoutId);

        if (!response.ok) {
          continue; // Try next proxy
        }

        const html = await response.text();

        // Skip processing if we got a Twitter login page
        if (isTwitterLoginPage(html)) {
          continue; // Try next proxy
        }
        if (isAccessDeniedPage(html)) {
          throw new Error("Access Denied page detected");
        }

        return { success: true, response, html };
      } catch (error) {
        clearTimeout(timeoutId);
        // Expected fetch error, try next proxy
        continue;
      }
    } catch (error) {
      // Continue to next proxy
    }
  }

  return { success: false };
};

// Common Twitter response formatter
const formatTwitterResponse = (name, username, text, imageUrl, twitterUrl) => {
  return {
    title:
      name && username ? `${name} (${username}) on Twitter` : "Twitter Post",
    description: text || "View this tweet on Twitter",
    image: imageUrl || null,
    url: twitterUrl,
  };
};

// Process Twitter API response
const processTwitterApiResponse = async (
  endpoint,
  twitterUrl,
  responseHandler
) => {
  try {
    const proxyUrl = `https://corsproxy.io/?${encodeURIComponent(endpoint)}`;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);

    try {
      const response = await fetch(proxyUrl, {
        headers: { "User-Agent": USER_AGENT },
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) return null;

      const data = await response.json();
      return responseHandler(data, twitterUrl);
    } catch (error) {
      clearTimeout(timeoutId);
      return null;
    }
  } catch (error) {
    return null;
  }
};

export const getMetaData = async (url) => {
  if (!url) return null;

  // Special handling for Twitter/X URLs
  if (url.includes("twitter.com") || url.includes("x.com")) {
    return await getTwitterMetadata(url);
  }

  // Special handling for YouTube URLs
  if (url.includes("youtube.com") || url.includes("youtu.be")) {
    return await getYoutubeMetadata(url);
  }

  try {
    // Try fetching with proxies first
    const { success, html } = await fetchWithProxies(url);

    if (success) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");
      return extractMetadata(doc, url);
    }

    // If all proxies failed, try a direct request as a last resort
    const axios = (await import("axios")).default;
    const response = await axios.get(url, {
      headers: {
        "User-Agent": USER_AGENT,
        Accept: "text/html",
      },
    });

    const directHtml = response.data;

    // Skip processing if we got a Twitter login page
    if (isTwitterLoginPage(directHtml)) {
      throw new Error("Twitter login page detected");
    }

    const parser = new DOMParser();
    const doc = parser.parseFromString(directHtml, "text/html");
    return extractMetadata(doc, url);
  } catch (error) {
    // If all methods fail, return a minimal result with just the URL
    return {
      title: null,
      description: null,
      image: null,
      url,
    };
  }
};

/**
 * Try to fetch a Twitter user's profile image using Nitter
 */
const getTwitterProfileImage = async (username) => {
  // Basic validation to avoid unnecessary API calls
  if (!username || username.length < 4 || !/^[a-z0-9_]+$/i.test(username)) {
    return DEFAULT_TWITTER_PROFILE_IMAGE;
  }

  const nitterInstances = [
    "https://nitter.net",
    "https://nitter.kavin.rocks",
    "https://nitter.it",
    "https://nitter.poast.org",
  ];

  for (const nitterBase of nitterInstances) {
    try {
      const nitterUrl = `${nitterBase}/${username}`;

      // Try fetching with proxies
      const proxies = [
        `https://corsproxy.io/?${encodeURIComponent(nitterUrl)}`,
        `https://api.allorigins.win/raw?url=${encodeURIComponent(nitterUrl)}`,
      ];

      let html = null;
      for (const proxyUrl of proxies) {
        try {
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 5000);

          try {
            const response = await fetch(proxyUrl, {
              headers: { "User-Agent": USER_AGENT },
              signal: controller.signal,
            });

            clearTimeout(timeoutId);

            if (response.ok) {
              html = await response.text();
              break;
            }
          } catch (error) {
            clearTimeout(timeoutId);
            // Expected error, continue to next proxy silently
          }
        } catch (err) {
          // Expected error, continue to next proxy silently
        }
      }

      if (!html) continue;

      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");

      // Try multiple selectors to find the profile image
      // Different Nitter instances may use slightly different HTML structures
      const selectors = [
        ".profile-card-avatar",
        ".profile-card img",
        ".profile-header img",
        ".avatar",
        ".tweet-avatar img",
        ".user-avatar img",
        ".profile img",
      ];

      let profileImgSrc = null;

      for (const selector of selectors) {
        const profileImg = doc.querySelector(selector);
        if (profileImg && profileImg.getAttribute("src")) {
          profileImgSrc = profileImg.getAttribute("src");
          break;
        }
      }

      if (profileImgSrc) {
        // Make image URL absolute
        const imgUrl = profileImgSrc.startsWith("http")
          ? profileImgSrc
          : new URL(profileImgSrc, nitterUrl).href;

        return imgUrl;
      }
    } catch (error) {
      // Continue to the next instance silently
    }
  }

  // If all Nitter instances fail, try a fallback method - use Twitter's API proxy
  try {
    const proxyUrl = `https://api.microlink.io/?url=${encodeURIComponent(
      `https://twitter.com/${username}`
    )}`;

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);

    try {
      const response = await fetch(proxyUrl, {
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (response.ok) {
        const data = await response.json();
        if (
          data.status === "success" &&
          data.data &&
          data.data.image &&
          data.data.image.url
        ) {
          return data.data.image.url;
        }
      }
    } catch (error) {
      clearTimeout(timeoutId);
      // Fallback silently failed
    }
  } catch (error) {
    // Fallback silently failed, continue to default image
  }

  return DEFAULT_TWITTER_PROFILE_IMAGE;
};

/**
 * Special handler for Twitter/X URLs
 * Twitter blocks most proxy requests, so we need custom handling
 */
const getTwitterMetadata = async (url) => {
  // Convert x.com URLs to twitter.com format for better compatibility
  const twitterUrl = url.replace("x.com", "twitter.com");

  // Check if it's a user profile URL (doesn't contain /status/)
  const isUserProfile = !twitterUrl.includes("/status/");
  if (isUserProfile) {
    // Extract username from URL
    // Format is twitter.com/username or twitter.com/username/
    const usernameMatch = twitterUrl.match(/twitter\.com\/([^/?#]+)/);
    if (usernameMatch && usernameMatch[1]) {
      const username = usernameMatch[1];

      // Skip special Twitter paths that aren't usernames
      if (
        [
          "home",
          "explore",
          "notifications",
          "messages",
          "i",
          "compose",
          "search",
          "settings",
        ].includes(username)
      ) {
        return formatTwitterResponse(
          null,
          null,
          "View this page on Twitter",
          null,
          twitterUrl
        );
      }

      // Validate username before making expensive API calls
      // Twitter usernames must be at least 4 characters and contain only letters, numbers, and underscores
      if (!username || username.length < 4 || !/^[a-z0-9_]+$/i.test(username)) {
        return formatTwitterResponse(
          null,
          null,
          "Twitter Profile",
          DEFAULT_TWITTER_PROFILE_IMAGE,
          twitterUrl
        );
      }

      // Try to get profile image
      const profileImage =
        (await getTwitterProfileImage(username)) ||
        DEFAULT_TWITTER_PROFILE_IMAGE;

      return {
        title: `@${username} on Twitter`,
        description: `View ${username}'s profile on Twitter`,
        image: profileImage,
        url: twitterUrl,
      };
    }
  }

  // Extract tweet ID for status posts
  const tweetIdMatch = twitterUrl.match(/\/status\/(\d+)/);
  if (!tweetIdMatch) {
    return formatTwitterResponse(
      null,
      null,
      "View this post on Twitter",
      null,
      twitterUrl
    );
  }

  const tweetId = tweetIdMatch[1];

  // Try multiple methods in sequence
  const twitterMetadataFetchers = [
    getNitterMetadata,
    getTwiiitMetadata,
    getThreadreaderMetadata,
    getTweetScraperMetadata,
  ];

  for (const fetcher of twitterMetadataFetchers) {
    const result = await fetcher(tweetId, twitterUrl);
    if (result) return result;
  }

  // If all attempts fail, return basic metadata
  return formatTwitterResponse(
    null,
    null,
    "View this tweet on Twitter",
    null,
    twitterUrl
  );
};

/**
 * Get Twitter metadata using Nitter instances
 */
const getNitterMetadata = async (tweetId, twitterUrl) => {
  const nitterInstances = [
    "https://nitter.net",
    "https://nitter.kavin.rocks",
    "https://nitter.it",
    "https://nitter.poast.org",
  ];

  for (const nitterBase of nitterInstances) {
    try {
      const nitterUrl = `${nitterBase}/i/status/${tweetId}`;

      // Try fetching with proxies
      const proxies = [
        `https://corsproxy.io/?${encodeURIComponent(nitterUrl)}`,
        `https://api.allorigins.win/raw?url=${encodeURIComponent(nitterUrl)}`,
      ];

      let html = null;
      for (const proxyUrl of proxies) {
        try {
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 5000);

          try {
            const response = await fetch(proxyUrl, {
              headers: { "User-Agent": USER_AGENT },
              signal: controller.signal,
            });

            clearTimeout(timeoutId);

            if (response.ok) {
              html = await response.text();
              break;
            }
          } catch (error) {
            clearTimeout(timeoutId);
            // Continue to next proxy
          }
        } catch (err) {
          // Continue to next proxy
        }
      }

      if (!html) continue;

      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");

      // Extract the tweet content
      const tweetTextElement = doc.querySelector(".tweet-content");
      const tweetText = tweetTextElement
        ? tweetTextElement.textContent.trim()
        : "";

      // Extract username and display name
      const usernameElement = doc.querySelector(".username");
      const username = usernameElement
        ? usernameElement.textContent.trim()
        : "";
      const nameElement = doc.querySelector(".fullname");
      const name = nameElement ? nameElement.textContent.trim() : "";

      // Get profile image
      const profileImg = doc.querySelector(".tweet-avatar img");
      const profileImgSrc = profileImg ? profileImg.getAttribute("src") : null;
      let imageUrl = null;

      // Check for media attachments
      const mediaContainer = doc.querySelector(".attachment");
      if (mediaContainer) {
        const mediaImg = mediaContainer.querySelector("img");
        if (mediaImg) {
          imageUrl = mediaImg.getAttribute("src");
          // Make image URL absolute
          if (imageUrl && !imageUrl.startsWith("http")) {
            imageUrl = new URL(imageUrl, nitterUrl).href;
          }
        }
      }

      // If we found the tweet content, return the metadata
      if (tweetText) {
        const finalImageUrl =
          imageUrl ||
          (profileImgSrc ? new URL(profileImgSrc, nitterUrl).href : null);
        return formatTwitterResponse(
          name,
          username,
          tweetText,
          finalImageUrl,
          twitterUrl
        );
      }
    } catch (error) {
      // Continue to the next instance
    }
  }

  return null;
};

/**
 * Get Twitter metadata using Twiiit.com API
 */
const getTwiiitMetadata = async (tweetId, twitterUrl) => {
  return processTwitterApiResponse(
    `https://twiiit.com/api/tweet/${tweetId}`,
    twitterUrl,
    (data, url) => {
      if (!data.tweet || !data.author) return null;

      const imageUrl =
        data.tweet.media && data.tweet.media.length > 0
          ? data.tweet.media[0].url
          : data.author.profile_image_url;

      return formatTwitterResponse(
        data.author.name,
        data.author.username,
        data.tweet.text,
        imageUrl,
        url
      );
    }
  );
};

/**
 * Get Twitter metadata using Threadreader API
 */
const getThreadreaderMetadata = async (tweetId, twitterUrl) => {
  return processTwitterApiResponse(
    `https://threadreaderapi.com/statuses/${tweetId}.json`,
    twitterUrl,
    (data, url) => {
      if (!data.content || !data.user) return null;

      const imageUrl =
        data.images && data.images.length > 0
          ? data.images[0]
          : data.user.profile_image_url;

      return formatTwitterResponse(
        data.user.name,
        data.user.screen_name,
        data.content,
        imageUrl,
        url
      );
    }
  );
};

/**
 * Get Twitter metadata using various public tweet scraping APIs
 */
const getTweetScraperMetadata = async (tweetId, twitterUrl) => {
  // We'll try several public API endpoints that can retrieve tweet info
  const scraperEndpoints = [
    // Public Microlink API - a service that provides metadata from any URL
    `https://api.microlink.io/?url=${encodeURIComponent(
      twitterUrl
    )}&screenshot=false`,

    // Public Tweet Scraper API by TweetPik
    `https://api.tweetpik.com/tweet-info/${tweetId}`,

    // Another serverless Twitter API
    `https://api.fxtwitter.com/status/${tweetId}`,
  ];

  for (const endpoint of scraperEndpoints) {
    try {
      const proxyUrl = endpoint.includes("microlink")
        ? endpoint
        : `https://corsproxy.io/?${encodeURIComponent(endpoint)}`;

      const response = await fetch(proxyUrl, {
        headers: { "User-Agent": USER_AGENT },
        timeout: 5000,
      });

      if (!response.ok) continue;

      const data = await response.json();

      if (endpoint.includes("microlink")) {
        // Handle Microlink API response
        if (data.status === "success" && data.data) {
          return formatTwitterResponse(
            null,
            null,
            data.data.description || "View this tweet on Twitter",
            data.data.image?.url,
            twitterUrl
          );
        }
      } else if (endpoint.includes("tweetpik")) {
        // Handle TweetPik API response
        if (data.tweet && data.user) {
          const imageUrl =
            data.tweet.media && data.tweet.media.length > 0
              ? data.tweet.media[0].url
              : data.user.profile_image_url;

          return formatTwitterResponse(
            data.user.name,
            data.user.screen_name,
            data.tweet.text,
            imageUrl,
            twitterUrl
          );
        }
      } else if (endpoint.includes("fxtwitter")) {
        // Handle fxtwitter API response
        if (data.tweet && data.user) {
          const imageUrl =
            data.tweet.media && data.tweet.media.length > 0
              ? data.tweet.media[0].url
              : data.user.profile_image_url;

          return formatTwitterResponse(
            data.user.name,
            data.user.screen_name,
            data.tweet.text,
            imageUrl,
            twitterUrl
          );
        }
      }
    } catch (error) {
      // Continue to next endpoint
    }
  }

  return null;
};

// Helper function to extract the first URL from text
export const extractFirstUrl = (text) => {
  if (!text) return null;

  // First try to match URLs with http/https protocols
  const protocolMatches = text.match(URL_UTILS.patterns.protocol);

  if (protocolMatches) {
    // Extract the URL part without the trailing space or punctuation
    return URL_UTILS.normalize(protocolMatches[0]);
  }

  // If no http/https URL found, try to match common domain patterns without protocol
  const domainMatches = text.match(URL_UTILS.patterns.domain);

  if (domainMatches) {
    // Add https:// prefix to the URL and remove any trailing space or punctuation
    return URL_UTILS.ensureProtocol(URL_UTILS.normalize(domainMatches[0]));
  }

  return null;
};

/**
 * Checks if the URL in the text is completed (user has finished typing it)
 * @param {string} text - The text to check for a completed URL
 * @returns {boolean} - True if the URL is completed (followed by space, newline, or at the end)
 */
export const isCompletedUrl = (text) => {
  if (!text) return false;

  // Quick check - if text ends with a space or newline, any URL in it is complete
  if (text.endsWith(" ") || text.endsWith("\n")) {
    return true;
  }

  // Use a simpler URL pattern for better performance
  const urlPattern =
    /(?<![\w@])(?:https?:\/\/)?(?:www\.)?[a-z0-9][-a-z0-9.]*\.[a-z]{2,}(?:\/[^\s]*)?/gi;

  const match = text.match(urlPattern);

  if (!match) return false;

  const url = match[0];
  const urlPosition = text.indexOf(url);

  // If URL is followed by a space or newline, it's complete
  if (urlPosition + url.length < text.length) {
    const afterUrl = text.substring(urlPosition + url.length);

    // Check first character after URL
    if (
      afterUrl[0] === " " ||
      afterUrl[0] === "\n" ||
      afterUrl.charCodeAt(0) === 10
    ) {
      return true;
    }

    // More efficient check for newlines in the remaining text
    if (
      afterUrl.includes("\n") ||
      afterUrl.indexOf(String.fromCharCode(10)) !== -1
    ) {
      return true;
    }
  }

  // If URL is not at the end of the text, it's considered complete
  if (text.trim() !== url && !text.trim().endsWith(url)) {
    return true;
  }

  // Special handling for Twitter/X URLs
  if (url.match(/https?:\/\/(twitter\.com|x\.com)/i)) {
    // For Twitter/X URLs, require at least a username with 4+ chars after the slash
    // Twitter usernames must be at least 4 characters
    if (url.match(/https?:\/\/(twitter\.com|x\.com)\/[a-z0-9_]{4,}/i)) {
      return true;
    }
    // Incomplete Twitter URL
    return false;
  }

  // Special handling for YouTube URLs
  if (url.match(/https?:\/\/(youtube\.com|youtu\.be)/i)) {
    // For youtube.com URLs, check various patterns
    if (
      url.includes("youtube.com/watch?v=") ||
      url.includes("youtube.com/live/") ||
      url.includes("youtube.com/embed/")
    ) {
      return true;
    }
    // For youtu.be URLs, require at least some characters after the domain
    if (url.match(/https?:\/\/youtu\.be\/[a-zA-Z0-9_-]{5,}/i)) {
      return true;
    }
    // Incomplete YouTube URL
    return false;
  }

  // URL is at the end of the text (possibly still being typed)
  // Consider it complete if it has a valid domain ending or contains a path

  // Check for common TLDs
  if (/\.(com|org|net|edu|gov|io|co|ai|app|dev)\b/i.test(url)) {
    return true;
  }

  // Check for a path after the domain
  if (/\.[a-z]{2,}\//.test(url)) {
    return true;
  }

  // Otherwise, it's probably still being typed
  return false;
};

/**
 * Detects if the HTML content is a Twitter login page
 * @param {string} html - The HTML content to check
 * @returns {boolean} - True if it's a Twitter login page
 */
const isTwitterLoginPage = (html) => {
  if (!html) return false;

  // Common indicators that we got a Twitter login page instead of content
  const loginPageIndicators = [
    "Sign in to X",
    "Sign in to Twitter",
    "Don't miss what's happening",
    "People on Twitter are the first to know",
    "Let's log you back in",
    "Forgot password?",
    "modal-onboarding",
    "prefers-color-scheme",
    "apple-touch-startup-image",
  ];

  // Check if the HTML contains multiple indicators of a login page
  let indicatorsFound = 0;
  for (const indicator of loginPageIndicators) {
    if (html.includes(indicator)) {
      indicatorsFound++;
      // If we find 3 or more indicators, we're confident it's a login page
      if (indicatorsFound >= 3) {
        return true;
      }
    }
  }

  return false;
};

/**
 * Detects if AccessDenied on a URL
 * @param {string} html - The HTML content to check
 * @returns {boolean} - True if there's an AccessDenied error
 */

const isAccessDeniedPage = (html) => {
  if (!html) return false;
  const accessDeniedIndicators = [
    "Access Denied",
    "You don't have permission to access",
  ];
  return accessDeniedIndicators.every((indicator) => html.includes(indicator));
};

// Function to download an image from a URL and convert it to a File object
export const downloadImageAsFile = async (imageUrl, fileName = "og-image") => {
  try {
    // Try fetching with proxies
    let response = null;

    for (let i = 0; i < CORS_PROXIES.length; i++) {
      try {
        const proxyUrl = CORS_PROXIES[i] + encodeURIComponent(imageUrl);

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);

        try {
          response = await fetch(proxyUrl, {
            signal: controller.signal,
          });

          clearTimeout(timeoutId);

          if (!response.ok) {
            // Expected failure with this proxy, try the next one
            continue;
          }

          break;
        } catch (error) {
          clearTimeout(timeoutId);
          response = null;
        }
      } catch (error) {
        response = null;
      }
    }

    if (!response || !response.ok) {
      return null; // All proxies failed, return silently
    }

    // Get the blob
    const blob = await response.blob();

    // Determine file extension from content type
    const contentType = response.headers.get("content-type");
    const extension = IMAGE_UTILS.getFileExtension(contentType);

    // Create a File object
    return new File([blob], `${fileName}.${extension}`, {
      type: contentType || "image/jpeg",
    });
  } catch (error) {
    // Silently fail and return null
    return null;
  }
};

/**
 * Uploads a file to the server using Vapor
 * @param {File} file - The file to upload
 * @param {string} token - Authentication token
 * @returns {Promise<Object>} - Upload result
 */
const uploadFileWithVapor = async (file, token) => {
  // Get upload URL from environment
  const apiHost = import.meta.env.VITE_API_HOST || "https://api.crystallize.cc";
  const uploadUrl = `${apiHost}/v3/media/signed-storage-url`;

  try {
    // Get file dimensions
    const dimensions = await IMAGE_UTILS.getDimensions(file);

    // Upload using Vapor
    return new Promise((resolve, reject) => {
      window.Vapor.store(file, {
        signedStorageUrl: uploadUrl,
        visibility: "public-read",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        progress: () => {
          // Progress tracking removed
        },
      })
        .then(async (response) => {
          // Register the upload with API
          const { apiBackendAuthAxios } = await import("@/axiosAuth");
          const encodedFileName = encodeURIComponent(file.name);

          try {
            const uploadResponse = await apiBackendAuthAxios.post(
              "/media/upload",
              {
                uuid: response.uuid,
                key: response.key,
                bucket: response.bucket,
                name: encodedFileName,
                content_type: file.type,
              }
            );

            resolve(
              createResult(true, {
                url: uploadResponse.data.url,
                width: dimensions.widthPx,
                height: dimensions.heightPx,
                fileId: uploadResponse.data.fileId || response.uuid,
              })
            );
          } catch (error) {
            reject(error);
          }
        })
        .catch((error) => {
          setTimeout(() => reject(error), 100);
        });
    });
  } catch (error) {
    return createResult(false, null, error);
  }
};

/**
 * Downloads and uploads an OpenGraph image silently without adding it to media arrays
 * @param {string} imageUrl - The URL of the image to download and upload
 * @return {Promise<{url: string, success: boolean}>} The URL of the uploaded image or null if failed
 */
export const uploadImageSilently = async (imageUrl) => {
  try {
    // Check if Vapor library is available
    if (typeof window.Vapor === "undefined") {
      return createResult(
        false,
        { url: imageUrl },
        "Vapor library not available"
      );
    }

    // Get authentication token
    const authResult = await getAuthToken();
    if (!authResult.success) {
      return createResult(false, { url: imageUrl }, authResult.error);
    }

    // Download the image file
    const file = await downloadImageAsFile(imageUrl, "og-preview");
    if (!file) {
      return createResult(false, { url: imageUrl }, "Image download failed");
    }

    // Test API endpoint (silently)
    try {
      await fetch(
        import.meta.env.VITE_API_HOST + "/v3/media/signed-storage-url",
        {
          method: "HEAD",
          headers: {
            Authorization: `Bearer ${authResult.token}`,
          },
          timeout: 3000,
        }
      );
    } catch (error) {
      // Fail silently, proceed with upload anyway
    }

    // Upload the file
    return await retry(
      async () => {
        const uploadResult = await uploadFileWithVapor(file, authResult.token);

        if (uploadResult.success) {
          return uploadResult;
        } else {
          throw new Error(uploadResult.error || "Upload failed");
        }
      },
      { maxAttempts: 2, delay: 300 }
    );
  } catch (error) {
    // Return the original URL as fallback
    return createResult(false, { url: imageUrl }, error);
  }
};

/**
 * Special handler for YouTube URLs
 * Extracts video ID and fetches metadata
 */
const getYoutubeMetadata = async (url) => {
  // Extract video ID from URL
  let videoId = null;
  let timestamp = null;
  let sessionId = null;

  // Parse full youtube.com URL (handles multiple formats)
  if (url.includes("youtube.com")) {
    const urlObj = new URL(url);

    // Standard watch URL format (youtube.com/watch?v=ID)
    videoId = urlObj.searchParams.get("v");

    // If not a standard watch URL, check for live stream or embed formats
    if (!videoId) {
      const pathname = urlObj.pathname;

      // Handle live stream URLs (youtube.com/live/ID)
      if (pathname.includes("/live/")) {
        const matches = pathname.match(/\/live\/([^/?&]+)/);
        if (matches && matches[1]) {
          videoId = matches[1];
        }
      }

      // Handle embed URLs (youtube.com/embed/ID)
      else if (pathname.includes("/embed/")) {
        const matches = pathname.match(/\/embed\/([^/?&]+)/);
        if (matches && matches[1]) {
          videoId = matches[1];
        }
      }
    }

    // Get timestamp parameter if it exists (can be 't' or 'start')
    const timeParam =
      urlObj.searchParams.get("t") || urlObj.searchParams.get("start");
    if (timeParam) {
      // Handle various timestamp formats (e.g., "10s", "1m30s", "1h2m3s", or just seconds like "90")
      timestamp = timeParam;
    }

    // Get session ID parameter (si) used for live streams and shared links
    const siParam = urlObj.searchParams.get("si");
    if (siParam) {
      sessionId = siParam;
    }
  }
  // Parse youtu.be short URL format
  else if (url.includes("youtu.be")) {
    const urlParts = url.split("/");
    const lastPart = urlParts[urlParts.length - 1];

    // Split by '?' to separate video ID from parameters
    const idAndParams = lastPart.split("?");
    videoId = idAndParams[0];

    // Check for parameters
    if (idAndParams.length > 1) {
      const params = new URLSearchParams(idAndParams[1]);

      // Get timestamp parameter
      const timeParam = params.get("t") || params.get("start");
      if (timeParam) {
        timestamp = timeParam;
      }

      // Get session ID parameter
      const siParam = params.get("si");
      if (siParam) {
        sessionId = siParam;
      }
    }
  }

  if (!videoId) {
    // Return minimal info if video ID extraction failed
    return {
      title: "YouTube Video",
      description: "Watch this video on YouTube",
      image: null,
      url,
      isYoutube: true,
      videoId: null,
    };
  }

  // Clean up video ID by removing any URL parameters
  videoId = videoId.split("&")[0];

  // Process timestamp to seconds for embed URL
  let startTime = "";
  if (timestamp) {
    // Convert timestamp to seconds for the embed URL
    if (typeof timestamp === "string") {
      // Handle time formats like "1h2m3s" or "1m30s"
      let seconds = 0;

      // Extract hours if present
      const hoursMatch = timestamp.match(/(\d+)h/);
      if (hoursMatch) {
        seconds += parseInt(hoursMatch[1]) * 3600;
      }

      // Extract minutes if present
      const minutesMatch = timestamp.match(/(\d+)m/);
      if (minutesMatch) {
        seconds += parseInt(minutesMatch[1]) * 60;
      }

      // Extract seconds if present
      const secondsMatch = timestamp.match(/(\d+)s/);
      if (secondsMatch) {
        seconds += parseInt(secondsMatch[1]);
      }

      // If no matches were found, try parsing the timestamp as a pure number of seconds
      if (seconds === 0 && /^\d+$/.test(timestamp)) {
        seconds = parseInt(timestamp);
      }

      if (seconds > 0) {
        startTime = `&start=${seconds}`;
      }
    }
  }

  // Add session ID parameter if present
  const siParam = sessionId ? `&si=${encodeURIComponent(sessionId)}` : "";

  // Try to fetch metadata using oEmbed API (public API that doesn't require a key)
  try {
    const oembedUrl = `https://www.youtube.com/oembed?url=${encodeURIComponent(
      url
    )}&format=json`;
    const proxyUrl = `https://corsproxy.io/?${encodeURIComponent(oembedUrl)}`;

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);

    try {
      const response = await fetch(proxyUrl, {
        headers: { "User-Agent": USER_AGENT },
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (response.ok) {
        const data = await response.json();

        // Create high-quality thumbnail URL
        // YouTube thumbnail options: default, hqdefault, mqdefault, sddefault, maxresdefault
        const thumbnailUrl = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;

        return {
          title: data.title || "YouTube Video",
          description: data.author_name
            ? `By ${data.author_name}`
            : "Watch this video on YouTube",
          image: thumbnailUrl,
          url,
          isYoutube: true,
          videoId,
          embedHtml: `<iframe width="560" height="315" src="https://www.youtube.com/embed/${videoId}${startTime}${siParam}" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>`,
        };
      }
    } catch (error) {
      clearTimeout(timeoutId);
      // Continue to fallback
    }
  } catch (error) {
    // Continue to fallback
  }

  // Fallback with basic info when API fails
  return {
    title: "YouTube Video",
    description: "Watch this video on YouTube",
    image: `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`,
    url,
    isYoutube: true,
    videoId,
    embedHtml: `<iframe width="560" height="315" src="https://www.youtube.com/embed/${videoId}${
      startTime || ""
    }${siParam}" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>`,
  };
};
